import type {
  AdminDashboardData,
  AdminDashboardSummary,
  AdminDashboardTodayData,
} from "@/admin/entities/adminDashboard";
import { INTERNAL_EXPENSE_CATEGORY_LABEL } from "@/admin/entities/internalExpenses";
import {
  computeTrend,
  formatCount,
  formatPercent,
  formatUsd,
  pluralRu,
  revenueSharePercent,
  trendTone,
  type TrendTone,
  type UnpaidSummary,
} from "./dashboardFormat";
import { organizationTitle } from "./tables";

export type MetricCardKey =
  | "sales"
  | "revenueShare"
  | "paidInvoices"
  | "expenses"
  | "netIncome"
  | "unpaid"
  | "licenses"
  | "clients";

export interface MetricCardDetail {
  text: string;
  tone?: "error" | "warning";
}

export interface MetricCardModel {
  key: MetricCardKey;
  label: string;
  value: string;
  trend: number | null;
  tone: TrendTone;
  /** Значение меньше нуля и показывается красным. */
  negative: boolean;
  details: MetricCardDetail[];
  /** Динамика по шагам периода; null у карточек «на сегодня». */
  sparkline: number[] | null;
  /** Маржа чистого дохода для Gauge; null, если оплат нет. */
  gauge: { value: number; label: string } | null;
}

export interface MetricCardsInput {
  dashboard: AdminDashboardData;
  today: AdminDashboardTodayData;
  unpaid: UnpaidSummary;
}

const INVOICE_FORMS: [string, string, string] = ["счёт", "счёта", "счетов"];
const RECORD_FORMS: [string, string, string] = ["запись", "записи", "записей"];
const PURCHASE_FORMS: [string, string, string] = [
  "покупка",
  "покупки",
  "покупок",
];
const CONTRACT_FORMS: [string, string, string] = [
  "контракт",
  "контракта",
  "контрактов",
];
const ORGANIZATION_FORMS: [string, string, string] = [
  "организация",
  "организации",
  "организаций",
];

const round1 = (value: number) => Math.round(value * 10) / 10;

/**
 * Восемь карточек (спека §3.2): первые пять за период с трендом и
 * спарклайном, «Ждём оплаты», «Лицензии в месяц» и «Клиенты» на сегодня.
 * Детализация по таблице спеки, «больше всего» берётся из строк
 * организаций и трат по статьям того же ответа.
 */
export function buildMetricCards(input: MetricCardsInput): MetricCardModel[] {
  const { dashboard, today, unpaid } = input;
  const current = dashboard.current;
  const previous = dashboard.previous;
  const trendOf = (pick: (summary: AdminDashboardSummary) => number) =>
    previous ? computeTrend(pick(current), pick(previous)) : null;
  const sparkOf = (
    pick: (point: AdminDashboardData["series"][number]) => number
  ) => dashboard.series.map(pick);

  const salesTrend = trendOf((summary) => summary.salesUSD);
  const revenueShareTrend = trendOf((summary) => summary.revenueShareUSD);
  const paidTrend = trendOf((summary) => summary.paidInvoicesUSD);
  const expensesTrend = trendOf((summary) => summary.expensesUSD);
  const netTrend = trendOf((summary) => summary.netIncomeUSD);

  const sharePercent = revenueSharePercent(
    current.revenueShareUSD,
    current.salesUSD
  );
  const topOrganization = [...dashboard.organizations]
    .filter((row) => row.revenueShareUSD > 0)
    .sort((left, right) => right.revenueShareUSD - left.revenueShareUSD)[0];
  const topCategory = dashboard.expensesByCategory.find(
    (row) => row.amountUSD > 0
  );
  const margin =
    current.paidInvoicesUSD > 0
      ? round1((current.netIncomeUSD / current.paidInvoicesUSD) * 100)
      : null;

  // Средний процент только при положительных чистых продажах; если оборот
  // был, но возвраты и диспуты его съели, «продаж нет» было бы неправдой.
  const revenueShareSummary =
    sharePercent !== null
      ? `в среднем ${formatPercent(sharePercent)} от продаж`
      : current.grossSalesUSD > 0
        ? "возвраты превысили продажи"
        : "продаж в периоде нет";
  const revenueShareDetails: MetricCardDetail[] = [
    { text: revenueShareSummary },
  ];
  if (topOrganization) {
    revenueShareDetails.push({
      text: `больше всего: ${organizationTitle(topOrganization.title)} ${formatUsd(topOrganization.revenueShareUSD)}`,
    });
  }

  const expensesDetails: MetricCardDetail[] = [
    {
      text: `${current.expensesCount} ${pluralRu(current.expensesCount, RECORD_FORMS)}`,
    },
  ];
  if (topCategory) {
    expensesDetails.push({
      text: `больше всего: ${INTERNAL_EXPENSE_CATEGORY_LABEL[topCategory.category] ?? topCategory.category} ${formatUsd(topCategory.amountUSD)}`,
    });
  }

  const netDetails: MetricCardDetail[] = [
    {
      text:
        margin === null
          ? "маржа: оплат в периоде нет"
          : `маржа ${formatPercent(margin)} от оплаченного`,
    },
  ];
  if (previous) {
    netDetails.push({
      text: `прошлый период: ${formatUsd(previous.netIncomeUSD)}`,
    });
  }

  return [
    {
      key: "sales",
      label: "Продажи клиентов",
      value: formatUsd(current.salesUSD),
      trend: salesTrend,
      tone: trendTone(salesTrend, "higherIsBetter"),
      negative: current.salesUSD < 0,
      details: [
        {
          text: `оборот ${formatUsd(current.grossSalesUSD)} · возвраты ${formatUsd(current.refundsUSD)} · диспуты ${formatUsd(current.disputesUSD)}`,
        },
        {
          text: `${current.purchasesCount} ${pluralRu(current.purchasesCount, PURCHASE_FORMS)}`,
        },
      ],
      sparkline: sparkOf((point) => point.salesUSD),
      gauge: null,
    },
    {
      key: "revenueShare",
      label: "Наш ревшер",
      value: formatUsd(current.revenueShareUSD),
      trend: revenueShareTrend,
      tone: trendTone(revenueShareTrend, "higherIsBetter"),
      negative: current.revenueShareUSD < 0,
      details: revenueShareDetails,
      sparkline: sparkOf((point) => point.revenueShareUSD),
      gauge: null,
    },
    {
      key: "paidInvoices",
      label: "Оплачено мне",
      value: formatUsd(current.paidInvoicesUSD),
      trend: paidTrend,
      tone: trendTone(paidTrend, "higherIsBetter"),
      negative: current.paidInvoicesUSD < 0,
      details: [
        {
          text: `ревшер ${formatUsd(current.paidRevenueShareUSD)} · лицензии ${formatUsd(current.paidLicensesUSD)}`,
        },
        {
          text: `${current.paidInvoicesCount} ${pluralRu(current.paidInvoicesCount, INVOICE_FORMS)}`,
        },
      ],
      sparkline: sparkOf((point) => point.paidInvoicesUSD),
      gauge: null,
    },
    {
      key: "expenses",
      label: "Траты",
      value: formatUsd(current.expensesUSD),
      trend: expensesTrend,
      tone: trendTone(expensesTrend, "higherIsWorse"),
      negative: false,
      details: expensesDetails,
      sparkline: sparkOf((point) => point.expensesUSD),
      gauge: null,
    },
    {
      key: "netIncome",
      label: "Чистый доход",
      value: formatUsd(current.netIncomeUSD),
      trend: netTrend,
      tone: trendTone(netTrend, "higherIsBetter"),
      negative: current.netIncomeUSD < 0,
      details: netDetails,
      sparkline: sparkOf((point) => point.netIncomeUSD),
      gauge:
        margin === null
          ? null
          : {
              value: Math.max(0, Math.min(100, margin)),
              label: formatPercent(margin),
            },
    },
    {
      key: "unpaid",
      label: "Ждём оплаты",
      value: formatUsd(unpaid.totalUSD),
      trend: null,
      tone: "neutral",
      negative: false,
      details: [
        { text: `${unpaid.count} ${pluralRu(unpaid.count, INVOICE_FORMS)}` },
        {
          text: `просрочено: ${formatUsd(unpaid.overdueUSD)}, ${unpaid.overdueCount} ${pluralRu(unpaid.overdueCount, INVOICE_FORMS)}`,
          ...(unpaid.overdueCount > 0 ? { tone: "error" as const } : {}),
        },
        // Без курса счёт считается в штуках, но не в долларах: итог занижен.
        ...(unpaid.withoutRateCount > 0
          ? [
              {
                text: `без курса: ${unpaid.withoutRateCount} ${pluralRu(unpaid.withoutRateCount, INVOICE_FORMS)}`,
                tone: "error" as const,
              },
            ]
          : []),
      ],
      sparkline: null,
      gauge: null,
    },
    {
      key: "licenses",
      label: "Лицензии в месяц",
      value: formatUsd(today.licensesMonthlyUSD),
      trend: null,
      tone: "neutral",
      negative: false,
      details: [
        {
          text: `${today.licenseContractsCount} ${pluralRu(today.licenseContractsCount, CONTRACT_FORMS)} с лицензией`,
        },
        // Подписанные контракты со стартом в будущем входят в сумму (спека
        // 2026-10-16 §3.3), строка объясняет, почему лицензии есть, а
        // действующих контрактов нет.
        ...(today.licenseContractsStartingLaterCount > 0
          ? [
              {
                text: `из них стартуют позже: ${today.licenseContractsStartingLaterCount}`,
              },
            ]
          : []),
        {
          text: `заканчиваются в 60 дней: ${today.endingSoonCount}`,
          ...(today.endingSoonCount > 0 ? { tone: "warning" as const } : {}),
        },
      ],
      sparkline: null,
      gauge: null,
    },
    {
      key: "clients",
      label: "Клиенты",
      value: formatCount(today.activeContractsCount),
      trend: null,
      tone: "neutral",
      negative: false,
      details: [
        {
          text: `с продажами за период: ${current.organizationsWithSalesCount} ${pluralRu(current.organizationsWithSalesCount, ORGANIZATION_FORMS)}`,
        },
        { text: `новых за период: ${current.newOrganizationsCount}` },
      ],
      sparkline: null,
      gauge: null,
    },
  ];
}
